'use client';

import { useState } from 'react';
import { X, Clock, RotateCcw, User, Sparkles, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DiscoveryVersion } from '@/hooks/use-discovery';

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  versions: DiscoveryVersion[];
  onRestore: (versionId: string) => Promise<void>;
  isRestoring: boolean;
  type: 'goals' | 'interviews' | 'competitors';
}

const sourceIcons: Record<string, React.ElementType> = {
  MANUAL: FileText,
  AI_GENERATED: Sparkles,
  RESTORED: RotateCcw,
  IMPORTED: FileText,
};

const sourceLabels: Record<string, string> = {
  MANUAL: 'Manual edit',
  AI_GENERATED: 'AI Generated',
  RESTORED: 'Restored',
  IMPORTED: 'Imported',
};

export function VersionHistoryModal({
  isOpen,
  onClose,
  versions,
  onRestore,
  isRestoring,
  type,
}: VersionHistoryModalProps) {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const handleRestore = async (versionId: string) => {
    setRestoringId(versionId);
    try {
      await onRestore(versionId);
      onClose();
    } finally {
      setRestoringId(null);
    }
  };

  if (!isOpen) return null;

  const getTypeName = () => {
    switch (type) {
      case 'goals':
        return 'Goals & Objectives';
      case 'interviews':
        return 'Stakeholder Interviews';
      case 'competitors':
        return 'Competitor Analysis';
      default:
        return type;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Version History</h2>
            <p className="text-sm text-gray-500 mt-0.5">{getTypeName()}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {versions.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No versions yet</h3>
              <p className="text-gray-600">Versions will be created automatically when you make changes.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {versions.map((version, index) => {
                const SourceIcon = sourceIcons[version.source] || FileText;
                const isLatest = index === 0;
                const isRestoreLoading = restoringId === version.id;

                return (
                  <div
                    key={version.id}
                    className={cn(
                      "border rounded-xl p-4 transition-all",
                      isLatest
                        ? "border-brand-primary/30 bg-brand-primary/5"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          isLatest ? "bg-brand-primary/10" : "bg-gray-100"
                        )}>
                          <SourceIcon className={cn(
                            "w-5 h-5",
                            isLatest ? "text-brand-primary" : "text-gray-500"
                          )} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">Version {version.version}</span>
                            {isLatest && (
                              <span className="px-2 py-0.5 bg-brand-primary/10 text-brand-primary text-xs font-medium rounded">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {new Date(version.createdAt).toLocaleString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-3.5 h-3.5" />
                              {version.createdBy?.name || 'Unknown'}
                            </span>
                          </div>
                          {version.changelog && (
                            <p className="text-sm text-gray-600 mt-2">{version.changelog}</p>
                          )}
                          <span className="inline-flex items-center gap-1 mt-2 text-xs text-gray-500">
                            <SourceIcon className="w-3 h-3" />
                            {sourceLabels[version.source]}
                          </span>
                        </div>
                      </div>
                      {!isLatest && (
                        <button
                          onClick={() => handleRestore(version.id)}
                          disabled={isRestoring}
                          className={cn(
                            "px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors",
                            isRestoreLoading
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          )}
                        >
                          {isRestoreLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4" />
                          )}
                          {isRestoreLoading ? 'Restoring...' : 'Restore'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



